/**
 * The page editor bridge
 * (docs/planning/agent-artifacts-phase2-plan-2026-09-24.md, sections 3.3,
 * 7.3 and 9.2).
 *
 * `GET /api/artifacts/:id/editor-source` answers the page with every body
 * element numbered `data-tb="e<N>"` and an editor content security policy
 * that allows exactly one script: this bridge, by its sha256. The client
 * builds the editing surface from that page:
 *
 *   web     <iframe sandbox="allow-scripts" srcdoc=...> (no allow-same-origin,
 *           so the page runs in an opaque origin), with the session meta and
 *           the bridge <script> inserted before </head> (withArtifactEditorSession)
 *   native  a WebView with JavaScript on, the page with the session meta
 *           (withArtifactEditorSession, script: false) and this source
 *           injected before the content loads
 *
 * The bridge has no dependencies. It reads the session token from
 * `<meta name="tabbio-editor-session">` (and removes the tag), stays inert
 * without one, marks text blocks (elements whose children are text or inline
 * phrasing), makes a clicked block editable (plain-text paste, Enter ends
 * editing in headings and other one-line blocks and inserts a line break
 * elsewhere, Escape leaves editing, Tab and Shift+Tab move between blocks,
 * no bold or italic shortcuts), never lets a link navigate while editing,
 * and posts `ready`, `element`, `selection`, `change`, `image`, `link` and
 * `limit` messages. Every message both ways carries the session token; the
 * bridge refuses a parent message without it (and, in an iframe, one from
 * any window but its parent), and the parent validates every frame message
 * with `parseArtifactEditorFrameMessage` (the frame's window, the token and
 * the zod schema below).
 *
 * Element ids: an op names elements by the ids of the version it is sent
 * against (`baseVersionId`). A `setInline` change keeps the `data-tb` ids of
 * the block's own inline elements, so they keep their identity. The ids do not change while a batch is built:
 * `apply` mirrors an op in the frame without renumbering (a duplicate has no
 * id until the next version). After a save, `page-edits` answers the ids
 * that changed (`ids`), which the parent passes on with `renumber`; after a
 * duplicate the parent reloads `editor-source`.
 *
 * Imported by the API, the mobile app and the native node:test runner, so
 * its only dependency is zod.
 */

import { z } from "zod";

export const ARTIFACT_EDITOR_BRIDGE_VERSION = 1 as const;

/** The meta tag the client adds with its session token. */
export const ARTIFACT_EDITOR_SESSION_META = "tabbio-editor-session";

/** A session token: 16 to 128 URL-safe characters (a UUID fits). */
export const ARTIFACT_EDITOR_TOKEN_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

/** Element ids of an annotated page (`data-tb`), as in `page-elements.ts`. */
export const ARTIFACT_EDITOR_ELEMENT_ID = /^e\d{1,6}$/;

/** Longest text a `setText` op carries (plan 5.2). */
export const PAGE_EDIT_TEXT_MAX = 4_000;
/** Longest inline markup a `setInline` op carries (plan 5.2). */
export const PAGE_EDIT_INLINE_MAX = 8_000;
/** Longest selected text a `selection` message carries. */
export const ARTIFACT_EDITOR_SELECTION_MAX = 2_000;

/**
 * The script. Its exact text is what the editor CSP hashes: never reformat
 * it without updating ARTIFACT_EDITOR_BRIDGE_SHA256 (the unit test pins it).
 */
export const ARTIFACT_EDITOR_BRIDGE_SOURCE = String.raw`(function () {
  "use strict";
  var w = window, d = document;
  if (w.__tabbioEditorBridge) return;
  w.__tabbioEditorBridge = 1;
  var VERSION = 1, OUT = "tabbio-editor", IN = "tabbio-editor-parent";
  var ID = /^e\d{1,6}$/, TOKEN = /^[A-Za-z0-9_-]{16,128}$/;
  var ACCENT = /^#[0-9a-fA-F]{3,8}$/;
  var MAX_TEXT = 4000, MAX_HTML = 8000, MAX_SELECTION = 2000;
  var PHRASING = {};
  "a abbr b bdi bdo br cite code data del dfn em i ins kbd mark q s samp small span strong sub sup time u var wbr".split(" ").forEach(function (t) { PHRASING[t] = 1; });
  var INLINE = {};
  "a b br code em i mark s small span strong sub sup u".split(" ").forEach(function (t) { INLINE[t] = 1; });
  var DROP = {};
  "script style template iframe object embed img svg math video audio picture canvas form input textarea select button link meta base noscript".split(" ").forEach(function (t) { DROP[t] = 1; });
  var ONE_LINE = /^(h[1-6]|a|button|summary|legend|label|dt|th|figcaption|caption)$/;
  var token = null, mode = "view", editing = null, focused = null;
  var pending = null, timer = 0, frame = 0, selected = false;
  var raf = w.requestAnimationFrame ? w.requestAnimationFrame.bind(w) : function (f) { return w.setTimeout(f, 16); };

  function tagOf(el) { return el.tagName.toLowerCase(); }
  function idOf(el) {
    var id = el && el.getAttribute ? el.getAttribute("data-tb") : null;
    return id && ID.test(id) ? id : null;
  }
  function byId(id) {
    return typeof id === "string" && ID.test(id) ? d.querySelector('[data-tb="' + id + '"]') : null;
  }
  function elementOf(node) {
    return node && node.nodeType === 1 ? node : node ? node.parentElement : null;
  }
  function rectOf(r) {
    return { x: Math.round(r.left), y: Math.round(r.top), width: Math.round(r.width), height: Math.round(r.height) };
  }
  function send(type, body) {
    if (!token) return;
    var msg = { source: OUT, v: VERSION, token: token, type: type };
    for (var k in body) if (Object.prototype.hasOwnProperty.call(body, k)) msg[k] = body[k];
    if (w.ReactNativeWebView && typeof w.ReactNativeWebView.postMessage === "function") {
      w.ReactNativeWebView.postMessage(JSON.stringify(msg));
    } else if (w.parent && w.parent !== w) {
      w.parent.postMessage(msg, "*");
    }
  }
  function safeHref(value) {
    var v = String(value).trim();
    return v.charAt(0) === "#" || /^(https?:\/\/|mailto:)/i.test(v);
  }
  function safeSrc(value) {
    return /^https:\/\//i.test(value) || /^data:image\/[a-z0-9.+-]+[;,]/i.test(value);
  }

  // ── text blocks ──────────────────────────────────────────────────────────
  function isTextBlock(el) {
    if (!idOf(el) || el.closest("svg")) return false;
    var tag = tagOf(el);
    if (tag === "img" || tag === "br" || tag === "hr" || tag === "svg") return false;
    if (!/\S/.test(el.textContent || "")) return false;
    var inner = el.querySelectorAll("*");
    for (var i = 0; i < inner.length; i++) if (!PHRASING[tagOf(inner[i])]) return false;
    return true;
  }
  function markBlocks() {
    var old = d.querySelectorAll("[data-tb-text]");
    for (var i = 0; i < old.length; i++) if (old[i] !== editing) old[i].removeAttribute("data-tb-text");
    var all = d.body ? d.body.querySelectorAll("[data-tb]") : [];
    var count = 0;
    for (var j = 0; j < all.length; j++) {
      var el = all[j];
      if (el.parentElement && el.parentElement.closest("[data-tb-text]")) continue;
      if (el === editing || isTextBlock(el)) { el.setAttribute("data-tb-text", ""); count++; }
    }
    return count;
  }
  function blocks() { return Array.prototype.slice.call(d.querySelectorAll("[data-tb-text]")); }

  // ── focus and editing ─────────────────────────────────────────────────────
  function postElement() {
    var el = editing || focused;
    var id = el && el.isConnected ? idOf(el) : null;
    if (!id) { send("element", { element: null }); return; }
    var tag = tagOf(el);
    var info = {
      id: id, tag: tag,
      role: tag === "img" ? "image" : tag === "a" ? "link" : el.hasAttribute("data-tb-text") ? "text" : "block",
      rect: rectOf(el.getBoundingClientRect()),
      editing: editing === el,
      canMoveUp: !!el.previousElementSibling, canMoveDown: !!el.nextElementSibling
    };
    if (tag === "img") info.alt = el.getAttribute("alt") || "";
    if (tag === "a") info.href = el.getAttribute("href") || "";
    send("element", { element: info });
  }
  function setFocus(el) {
    if (focused && focused !== el) focused.removeAttribute("data-tb-focus");
    focused = el;
    if (el) el.setAttribute("data-tb-focus", "");
    postElement();
  }
  function startEditing(el) {
    if (editing === el) return;
    stopEditing(true);
    editing = el;
    try { el.contentEditable = "plaintext-only"; } catch (e) { /* older engines */ }
    if (el.contentEditable !== "plaintext-only") el.contentEditable = "true";
    el.setAttribute("spellcheck", "true");
    if (el.focus) el.focus();
    setFocus(el);
  }
  function stopEditing(quiet) {
    if (!editing) return;
    flush();
    var el = editing;
    editing = null;
    el.removeAttribute("contenteditable");
    el.removeAttribute("spellcheck");
    if (el.blur) el.blur();
    if (!quiet) postElement();
  }
  function clean(el) {
    var copy = el.cloneNode(true);
    var all = copy.querySelectorAll("*");
    for (var i = 0; i < all.length; i++) {
      ["data-tb-text", "data-tb-focus", "contenteditable", "spellcheck"].forEach(function (a) { all[i].removeAttribute(a); });
    }
    return copy.innerHTML;
  }
  function opOf(el) {
    var id = idOf(el);
    if (!id) return null;
    if (el.children.length === 0) {
      var text = el.textContent || "";
      return text.length > MAX_TEXT ? { limit: MAX_TEXT, id: id } : { op: "setText", id: id, text: text };
    }
    var html = clean(el);
    return html.length > MAX_HTML ? { limit: MAX_HTML, id: id } : { op: "setInline", id: id, html: html };
  }
  function schedule() {
    pending = editing;
    if (!timer) timer = w.setTimeout(flush, 250);
  }
  function flush() {
    if (timer) { w.clearTimeout(timer); timer = 0; }
    var el = pending;
    pending = null;
    if (!el) return;
    var op = opOf(el);
    if (!op) return;
    if (op.limit) send("limit", { id: op.id, max: op.limit });
    else send("change", { op: op });
  }
  function insertText(text) {
    if (d.execCommand && d.execCommand("insertText", false, text)) return;
    var sel = w.getSelection();
    if (!sel || !sel.rangeCount) return;
    var range = sel.getRangeAt(0);
    range.deleteContents();
    var node = d.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    schedule();
  }
  function insertBreak() {
    if (d.execCommand && d.execCommand("insertLineBreak")) return;
    var sel = w.getSelection();
    if (!sel || !sel.rangeCount) return;
    var range = sel.getRangeAt(0);
    range.deleteContents();
    var br = d.createElement("br");
    range.insertNode(br);
    range.setStartAfter(br);
    range.collapse(true);
    sel.removeAllRanges();
    sel.addRange(range);
    schedule();
  }
  function move(step) {
    var list = blocks();
    var at = list.indexOf(editing || focused);
    var next = list[at < 0 ? (step > 0 ? 0 : list.length - 1) : at + step];
    if (!next) return;
    if (next.scrollIntoView) next.scrollIntoView({ block: "nearest" });
    startEditing(next);
  }

  // ── selection ─────────────────────────────────────────────────────────────
  function postSelection() {
    var sel = w.getSelection();
    var range = sel && sel.rangeCount && !sel.isCollapsed ? sel.getRangeAt(0) : null;
    var holder = range ? elementOf(range.commonAncestorContainer) : null;
    holder = holder ? holder.closest("[data-tb-text]") || holder.closest("[data-tb]") : null;
    var text = range ? sel.toString().slice(0, MAX_SELECTION) : "";
    if (!holder || !/\S/.test(text)) {
      if (selected) { selected = false; send("selection", { id: null, text: "", rect: null }); }
      return;
    }
    selected = true;
    send("selection", { id: idOf(holder), text: text, rect: rectOf(range.getBoundingClientRect()) });
  }
  function reposition() {
    if (frame) return;
    frame = raf(function () {
      frame = 0;
      if (mode !== "edit") return;
      if (editing || focused) postElement();
      postSelection();
    });
  }

  // ── ops from the parent ───────────────────────────────────────────────────
  function sanitizeInline(html) {
    var box = d.createElement("template");
    box.innerHTML = html;
    var root = box.content || box;
    (function walk(parent) {
      var node = parent.firstChild;
      while (node) {
        var next = node.nextSibling;
        if (node.nodeType === 1) {
          var tag = tagOf(node);
          if (DROP[tag]) { parent.removeChild(node); }
          else if (!INLINE[tag]) {
            walk(node);
            while (node.firstChild) parent.insertBefore(node.firstChild, node);
            parent.removeChild(node);
          } else {
            Array.prototype.slice.call(node.attributes).forEach(function (a) {
              var name = a.name.toLowerCase();
              var keep = name === "class" || name === "style" || name === "lang" || name === "dir" || name === "title" || (name === "href" && tag === "a" && safeHref(a.value));
              if (!keep) node.removeAttribute(a.name);
            });
            walk(node);
          }
        } else if (node.nodeType !== 3) {
          parent.removeChild(node);
        }
        node = next;
      }
    })(root);
    return root;
  }
  function stripIds(el) {
    var all = [el].concat(Array.prototype.slice.call(el.querySelectorAll("*")));
    all.forEach(function (n) {
      ["data-tb", "data-tb-text", "data-tb-focus", "contenteditable", "spellcheck"].forEach(function (a) { n.removeAttribute(a); });
    });
  }
  function apply(op) {
    if (!op || typeof op !== "object") return;
    var el = byId(op.id);
    if (!el || !el.parentNode) return;
    if (el === editing && op.op !== "setAttr") stopEditing(true);
    switch (op.op) {
      case "setText":
        if (typeof op.text !== "string") return;
        el.textContent = "";
        op.text.split("\n").forEach(function (part, i) {
          if (i > 0) el.appendChild(d.createElement("br"));
          if (part) el.appendChild(d.createTextNode(part));
        });
        break;
      case "setInline":
        if (typeof op.html !== "string") return;
        el.textContent = "";
        el.appendChild(sanitizeInline(op.html));
        break;
      case "setAttr":
        if ((op.name !== "href" && op.name !== "alt" && op.name !== "title") || typeof op.value !== "string") return;
        if (op.name === "href" && !safeHref(op.value)) return;
        if (op.name === "title" && op.value === "") el.removeAttribute("title");
        else el.setAttribute(op.name, op.value);
        break;
      case "replaceImage":
        if (tagOf(el) !== "img" || typeof op.displaySrc !== "string" || !safeSrc(op.displaySrc)) return;
        el.setAttribute("src", op.displaySrc);
        el.removeAttribute("srcset");
        el.removeAttribute("width");
        el.removeAttribute("height");
        if (typeof op.alt === "string") el.setAttribute("alt", op.alt);
        break;
      case "remove":
        if (focused === el) focused = null;
        el.parentNode.removeChild(el);
        break;
      case "duplicate":
        var copy = el.cloneNode(true);
        stripIds(copy);
        el.parentNode.insertBefore(copy, el.nextSibling);
        break;
      case "move":
        var sibling = op.direction === "up" ? el.previousElementSibling : op.direction === "down" ? el.nextElementSibling : null;
        if (!sibling) return;
        if (op.direction === "up") el.parentNode.insertBefore(el, sibling);
        else el.parentNode.insertBefore(sibling, el);
        break;
      default:
        return;
    }
    markBlocks();
    postElement();
  }
  function renumber(ids) {
    if (!ids || typeof ids !== "object") return;
    var nodes = d.querySelectorAll("[data-tb]"), moves = [];
    for (var i = 0; i < nodes.length; i++) {
      var id = nodes[i].getAttribute("data-tb");
      if (Object.prototype.hasOwnProperty.call(ids, id)) moves.push([nodes[i], ids[id]]);
    }
    moves.forEach(function (m) {
      if (m[1] === null) m[0].removeAttribute("data-tb");
      else if (typeof m[1] === "string" && ID.test(m[1])) m[0].setAttribute("data-tb", m[1]);
    });
    markBlocks();
    postElement();
  }
  function setMode(next, accent) {
    mode = next === "edit" ? "edit" : "view";
    var root = d.documentElement;
    if (mode === "edit") root.classList.add("tb-edit");
    else { root.classList.remove("tb-edit"); stopEditing(true); setFocus(null); }
    if (typeof accent === "string" && ACCENT.test(accent)) root.style.setProperty("--tb-accent", accent);
  }
  function onMessage(event) {
    var data = event.data;
    if (typeof data === "string") { try { data = JSON.parse(data); } catch (e) { return; } }
    if (!data || typeof data !== "object" || data.source !== IN || data.v !== VERSION) return;
    if (!token || data.token !== token) return;
    if (w.parent !== w && event.source !== w.parent) return;
    switch (data.type) {
      case "setMode": setMode(data.mode, data.accent); break;
      case "focus":
        var el = byId(data.id);
        if (!el || mode !== "edit") return;
        if (el.scrollIntoView) el.scrollIntoView({ block: "center" });
        if (data.edit && el.hasAttribute("data-tb-text")) startEditing(el);
        else { stopEditing(true); setFocus(el); }
        break;
      case "blur": stopEditing(true); setFocus(null); break;
      case "apply": apply(data.op); break;
      case "renumber": renumber(data.ids); break;
    }
  }

  // ── events ────────────────────────────────────────────────────────────────
  function onClick(event) {
    var target = elementOf(event.target);
    if (!target) return;
    var link = target.closest("a");
    if (link && (mode === "edit" || (link.getAttribute("href") || "").charAt(0) !== "#")) event.preventDefault();
    if (mode !== "edit") return;
    var image = target.closest("img[data-tb]");
    if (image) {
      stopEditing(true);
      setFocus(image);
      send("image", { id: idOf(image), src: image.getAttribute("src") || "", alt: image.getAttribute("alt") || "", rect: rectOf(image.getBoundingClientRect()) });
      return;
    }
    if (link && idOf(link)) {
      send("link", { id: idOf(link), href: link.getAttribute("href") || "", text: (link.textContent || "").slice(0, MAX_SELECTION), rect: rectOf(link.getBoundingClientRect()) });
    }
    var block = target.closest("[data-tb-text]");
    if (block) { startEditing(block); return; }
    var holder = target.closest("[data-tb]");
    stopEditing(true);
    setFocus(holder);
  }
  function onKey(event) {
    if (mode !== "edit") return;
    var key = event.key;
    if (key === "Tab") { event.preventDefault(); move(event.shiftKey ? -1 : 1); return; }
    if (!editing) return;
    if (key === "Escape") { event.preventDefault(); stopEditing(); return; }
    if ((event.metaKey || event.ctrlKey) && /^[biu]$/i.test(key)) { event.preventDefault(); return; }
    if (key === "Enter") {
      event.preventDefault();
      if (ONE_LINE.test(tagOf(editing))) stopEditing();
      else insertBreak();
    }
  }
  function init() {
    var meta = d.querySelector('meta[name="tabbio-editor-session"]');
    var value = meta ? meta.getAttribute("content") : null;
    if (meta && meta.parentNode) meta.parentNode.removeChild(meta);
    if (!value || !TOKEN.test(value)) return;
    token = value;
    var style = d.createElement("style");
    style.textContent = "html.tb-edit [data-tb-text]{cursor:text}" +
      "html.tb-edit [data-tb-text]:hover,html.tb-edit img[data-tb]:hover,html.tb-edit [data-tb-focus],html.tb-edit [contenteditable]" +
      "{outline:2px solid var(--tb-accent,#10a37f);outline-offset:2px}";
    (d.head || d.documentElement).appendChild(style);
    d.addEventListener("click", onClick, true);
    d.addEventListener("auxclick", function (event) { if (elementOf(event.target) && elementOf(event.target).closest("a")) event.preventDefault(); }, true);
    d.addEventListener("keydown", onKey, true);
    d.addEventListener("input", function (event) { if (editing && editing.contains(event.target)) schedule(); }, true);
    d.addEventListener("beforeinput", function (event) { if (editing && /^format/.test(event.inputType || "")) event.preventDefault(); }, true);
    d.addEventListener("paste", function (event) {
      if (!editing) return;
      event.preventDefault();
      var text = event.clipboardData ? event.clipboardData.getData("text/plain") : "";
      insertText(String(text || "").replace(/\s*[\r\n]+\s*/g, " "));
    }, true);
    d.addEventListener("drop", function (event) { if (mode === "edit") event.preventDefault(); }, true);
    d.addEventListener("focusout", function (event) { if (editing && event.target === editing) flush(); }, true);
    d.addEventListener("selectionchange", function () { if (mode === "edit") reposition(); });
    w.addEventListener("scroll", reposition, true);
    w.addEventListener("resize", reposition);
    w.addEventListener("message", onMessage);
    d.addEventListener("message", onMessage);
    send("ready", { blocks: markBlocks() });
  }
  if (d.readyState === "loading") d.addEventListener("DOMContentLoaded", init);
  else init();
})();`;

/**
 * The script's sha256, base64, for `script-src 'sha256-...'`. Pinned by
 * artifact-editor-bridge.test.ts: a changed script needs a new hash.
 */
export const ARTIFACT_EDITOR_BRIDGE_SHA256 =
  "l6Z7LSGehg7PNrWNm8M/YxQe0MHMO4cx0qze7NiTK9k=";

/** The CSP source that allows the bridge and nothing else. */
export const ARTIFACT_EDITOR_BRIDGE_CSP_SOURCE = `'sha256-${ARTIFACT_EDITOR_BRIDGE_SHA256}'`;

// ── Messages ───────────────────────────────────────────────────────────────

const elementId = z.string().regex(ARTIFACT_EDITOR_ELEMENT_ID);
const token = z.string().regex(ARTIFACT_EDITOR_TOKEN_PATTERN);
const rect = z.object({
  x: z.number().finite(),
  y: z.number().finite(),
  width: z.number().finite().min(0),
  height: z.number().finite().min(0),
});

/**
 * One manual edit of a page (plan 5.2), shared by the client's op queue,
 * the frame messages and the API's `page-edits` body.
 */
export const pageEditOpSchema = z.discriminatedUnion("op", [
  z.object({
    op: z.literal("setInline"),
    id: elementId,
    html: z.string().max(PAGE_EDIT_INLINE_MAX),
  }),
  z.object({
    op: z.literal("setText"),
    id: elementId,
    text: z.string().max(PAGE_EDIT_TEXT_MAX),
  }),
  z.object({
    op: z.literal("setAttr"),
    id: elementId,
    name: z.enum(["href", "alt", "title"]),
    value: z.string().max(2_048),
  }),
  z.object({
    op: z.literal("replaceImage"),
    id: elementId,
    source: z.discriminatedUnion("type", [
      z.object({
        type: z.literal("artifact"),
        artifactId: z.string().trim().min(1).max(64),
      }),
      z.object({ type: z.literal("url"), url: z.string().url().max(2_048) }),
    ]),
    alt: z.string().max(300).optional(),
  }),
  z.object({ op: z.literal("remove"), id: elementId }),
  z.object({ op: z.literal("duplicate"), id: elementId }),
  z.object({
    op: z.literal("move"),
    id: elementId,
    direction: z.enum(["up", "down"]),
  }),
]);

export type PageEditOpInput = z.infer<typeof pageEditOpSchema>;

const frameBase = {
  source: z.literal("tabbio-editor"),
  v: z.literal(ARTIFACT_EDITOR_BRIDGE_VERSION),
  token,
};

/** What the frame posts to the parent. */
export const artifactEditorFrameMessageSchema = z.discriminatedUnion("type", [
  z.object({
    ...frameBase,
    type: z.literal("ready"),
    blocks: z.number().int().min(0),
  }),
  z.object({
    ...frameBase,
    type: z.literal("element"),
    element: z
      .object({
        id: elementId,
        tag: z.string().regex(/^[a-z][a-z0-9-]{0,31}$/),
        role: z.enum(["text", "image", "link", "block"]),
        rect,
        editing: z.boolean(),
        canMoveUp: z.boolean(),
        canMoveDown: z.boolean(),
        alt: z.string().max(2_048).optional(),
        href: z.string().max(4_096).optional(),
      })
      .nullable(),
  }),
  z.object({
    ...frameBase,
    type: z.literal("selection"),
    id: elementId.nullable(),
    text: z.string().max(ARTIFACT_EDITOR_SELECTION_MAX),
    rect: rect.nullable(),
  }),
  z.object({
    ...frameBase,
    type: z.literal("change"),
    op: z.discriminatedUnion("op", [
      z.object({
        op: z.literal("setText"),
        id: elementId,
        text: z.string().max(PAGE_EDIT_TEXT_MAX),
      }),
      z.object({
        op: z.literal("setInline"),
        id: elementId,
        html: z.string().max(PAGE_EDIT_INLINE_MAX),
      }),
    ]),
  }),
  z.object({
    ...frameBase,
    type: z.literal("image"),
    id: elementId,
    src: z.string().max(12_000_000),
    alt: z.string().max(2_048),
    rect,
  }),
  z.object({
    ...frameBase,
    type: z.literal("link"),
    id: elementId,
    href: z.string().max(4_096),
    text: z.string().max(ARTIFACT_EDITOR_SELECTION_MAX),
    rect,
  }),
  z.object({
    ...frameBase,
    type: z.literal("limit"),
    id: elementId,
    max: z.number().int().positive(),
  }),
]);

export type ArtifactEditorFrameMessage = z.infer<
  typeof artifactEditorFrameMessageSchema
>;

const parentBase = {
  source: z.literal("tabbio-editor-parent"),
  v: z.literal(ARTIFACT_EDITOR_BRIDGE_VERSION),
  token,
};

/**
 * An op mirrored in the frame (`apply`). A replaced image needs something
 * the frame can show: `displaySrc` (https or a data image), since a
 * `tabbio-artifact:` source only resolves on the server.
 */
const appliedOpSchema = z.union([
  pageEditOpSchema.options[0],
  pageEditOpSchema.options[1],
  pageEditOpSchema.options[2],
  pageEditOpSchema.options[3].extend({
    displaySrc: z
      .string()
      .max(12_000_000)
      .regex(/^(https:\/\/|data:image\/[a-z0-9.+-]+[;,])/i),
  }),
  pageEditOpSchema.options[4],
  pageEditOpSchema.options[5],
  pageEditOpSchema.options[6],
]);

/** What the parent posts to the frame. */
export const artifactEditorParentMessageSchema = z.discriminatedUnion("type", [
  z.object({
    ...parentBase,
    type: z.literal("setMode"),
    mode: z.enum(["edit", "view"]),
    accent: z
      .string()
      .regex(/^#[0-9a-fA-F]{3,8}$/)
      .optional(),
  }),
  z.object({
    ...parentBase,
    type: z.literal("focus"),
    id: elementId,
    edit: z.boolean().optional(),
  }),
  z.object({ ...parentBase, type: z.literal("blur") }),
  z.object({ ...parentBase, type: z.literal("apply"), op: appliedOpSchema }),
  z.object({
    ...parentBase,
    type: z.literal("renumber"),
    ids: z.record(elementId, elementId.nullable()),
  }),
]);

export type ArtifactEditorParentMessage = z.infer<
  typeof artifactEditorParentMessageSchema
>;

/**
 * A frame message the parent may act on: posted by the editing frame's own
 * window (`expectedSource`, when the platform gives one), with this session's
 * token and a valid body. Anything else is null and must be ignored.
 */
export function parseArtifactEditorFrameMessage(
  data: unknown,
  session: { token: string; source?: unknown; expectedSource?: unknown },
): ArtifactEditorFrameMessage | null {
  if (
    session.expectedSource !== undefined &&
    session.source !== session.expectedSource
  ) {
    return null;
  }
  let value = data;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value) as unknown;
    } catch {
      return null;
    }
  }
  const parsed = artifactEditorFrameMessageSchema.safeParse(value);
  if (!parsed.success || parsed.data.token !== session.token) return null;
  return parsed.data;
}

/** The meta tag and, for web, the bridge script, before `</head>`. */
export function withArtifactEditorSession(
  html: string,
  sessionToken: string,
  options: { script?: boolean } = {},
): string {
  if (!ARTIFACT_EDITOR_TOKEN_PATTERN.test(sessionToken)) {
    throw new Error(
      "The editor session token must be 16 to 128 URL-safe characters.",
    );
  }
  const tags = `<meta name="${ARTIFACT_EDITOR_SESSION_META}" content="${sessionToken}">${
    options.script === false
      ? ""
      : `<script>${ARTIFACT_EDITOR_BRIDGE_SOURCE}</script>`
  }`;
  const headEnd = html.search(/<\/head\s*>/i);
  if (headEnd >= 0)
    return `${html.slice(0, headEnd)}${tags}${html.slice(headEnd)}`;
  const bodyStart = html.search(/<body[\s>]/i);
  if (bodyStart >= 0)
    return `${html.slice(0, bodyStart)}${tags}${html.slice(bodyStart)}`;
  return `${tags}${html}`;
}
